<template>
  <WizardDialog
    ref="dialog"
    :title="title"
    :submitting="submitting"
    :icon="icon"
    :form="dialogForm"
    sizeClass="modal-xxl modal-h80"
    :wizardTabList="wizardTabList"
    :loading="loading"
    :loadingError="loadingError"
    :finishDisabled="finishDisabled"
    @stepChange="wizardStepChanged"
    @closed="onClosed"
    @submitted="onSubmitted">

    <template v-slot:content>
      <!-- Task Type Step -->
      <FormWizardStep stepid="tasktype">
        <ContentTitle>Task Type</ContentTitle>
        <FormRow label="Task Type">
          <FormSelect v-model="task.taskTypeId" placeholder="Select task type" :options="activeTaskTypeLookup" />
        </FormRow>
        <FormRow label="Account Location">
          <FormSelect v-model="task.accountLocationId" :options="accountLocationLookup" field="accountLocationId" />
        </FormRow>
        <div v-if="isSingleContactTaskType">
          <ContentTitle>Contact Required</ContentTitle>
          <FormRow label="Contact"> 
            <ContactSearchableSelect 
              :isCustomer="true" 
              :activeOnly="true" 
              v-model="selectContactId" 
              @updateContactInfo="onContactInfoUpdated" 
              field="contactId" 
            /> 
          </FormRow>
        </div>
      </FormWizardStep>

      <!-- Orders Step -->
      <FormWizardStep v-if="requiresOrderSelection" stepid="orders">
        <div v-if="!isSingleContactTaskType || hasContactSelected">
          <TaskDespatchOrderDataTable
            v-model="selectedOrderIdList"
            @update="orderListUpdated"
            :openNewTab="true"
            :despatchTaskTypeId="taskType.id"
            :filterContactId="filterContactId"
            :filterAccountLocationId="parseInt(task.accountLocationId)"
          >
            <template v-slot:title>
              <ContentTitle>Available Order List</ContentTitle>
            </template>
          </TaskDespatchOrderDataTable>
        </div>

        <div v-if="isSingleContactTaskType && !hasContactSelected" class="text-center font-italic">A contact must be selected for this task type before continuing</div>
      </FormWizardStep>

      <FormWizardStep v-if="hasPanelTransferSelection" stepid="panels">
        <div v-if="!isSingleContactTaskType || hasContactSelected">
          <TaskPanelTransferDataTable 
            v-model="selectedPanelIdList"
            :openNewTab="true"
            @update="panelListUpdated"
            :filterAccountLocationId="parseInt(task.accountLocationId)"
          >
            <template v-slot:title>
              <ContentTitle>Available Panel Transfer List</ContentTitle>
            </template>
          </TaskPanelTransferDataTable>          
        </div>
        <div v-if="isSingleContactTaskType && !hasContactSelected" class="text-center font-italic">A contact must be selected for this task type before continuing</div>
      </FormWizardStep>

      <FormWizardStep v-if="hasStockTransferSelection" stepid="stock">
        <TaskStockTransferDataTable 
          :openNewTab="true"
          @stockTransferAdded="addStockTransferToRunsheet"
        >
          <template v-slot:title>
            <ContentTitle>Stock Transfer List</ContentTitle>
          </template>
        </TaskStockTransferDataTable>
      </FormWizardStep>

      <FormWizardStep v-if="hasPurchaseOrderPickupSelection" stepid="popickup">
        <TaskPurchaseOrderPickupDataTable 
          :despatchTaskTypeId="taskType.id"
          :pickupPOList="selectedPickupPOIdList"
          @updatePickupList="selectedPickupPOIdList = $event; purchaseOrderListUpdated()"
          :despatchPOList="selectedDespatchPOIdList"
          @updateDespatchList="selectedDespatchPOIdList = $event; purchaseOrderListUpdated()"
          :openNewTab="true"
          :filterAccountLocationId="parseInt(task.accountLocationId)"
        >
          <template v-slot:title>
            <ContentTitle>Available Purchase Order Pickup List</ContentTitle>
          </template>
        </TaskPurchaseOrderPickupDataTable>
      </FormWizardStep>


      <!-- Run Sheet Step -->      
      <FormWizardStep v-if="requiresOrderSelection || hasPanelTransferSelection" stepid="runsheet">
        <LoadingPanel :loading="runSheet.loading" :loadingError="runSheet.error" loadingText="Loading Runsheet Data" />

        <div v-if="runSheetHasItems && !runSheet.loading && !isMergingTasks && isLocationTrackingEnabled">
          <ContentTitle>Route Details</ContentTitle>

          <FormRow label="Route Start Location" :fieldCount="2">
            <template v-slot:field1>
              <FormSelect v-model="task.routeStartLocationType" :options="TaskRouteLocationTypeLookup" field="routeStartLocationType" note="Select the location type for the start of the route" />
            </template>
            <template v-slot:field2>
              <FormInputAddressSearch v-show="task.routeStartLocationType == TaskRouteLocationTypes.address" v-model="task.routeStartLocationAddress" :splitAddressComponents="false" placeholder="Address" field="routeStartLocationAddress" />
              <FormSelect v-show="task.routeStartLocationType == TaskRouteLocationTypes.accountLocation" v-model="task.startPointAccountLocationId" :options="accountLocationLookup" field="startPointAccountLocationId" />
            </template>
          </FormRow>

          <FormRow label="Route End Location" :fieldCount="2">
            <template v-slot:field1>
              <FormSelect v-model="task.routeEndLocationType" :options="TaskRouteLocationTypeLookup" field="routeEndLocationType"  note="Select the location type for the end of the route after completing the items" />
            </template>
            <template v-slot:field2>
              <FormInputAddressSearch v-show="task.routeEndLocationType == TaskRouteLocationTypes.address" v-model="task.routeEndLocationAddress" :splitAddressComponents="false" placeholder="Address" field="routeEndLocationAddress" />
              <FormSelect v-show="task.routeEndLocationType == TaskRouteLocationTypes.accountLocation" v-model="task.endPointAccountLocationId" :options="accountLocationLookup" field="endPointAccountLocationId" />
            </template>
          </FormRow>
          <FormHorizontalRule />
        </div>

        <div class="row" v-if="runSheetHasItems">
          <div class="col-lg-12">
            <div class="float-right">
              <ActionDropMenu>
                <DropdownMenuItem @click="runsheetExpandAll" icon="fa-expand">Expand All</DropdownMenuItem>
                <DropdownMenuItem @click="runsheetCollapseAll" icon="fa-compress">Collapse All</DropdownMenuItem>
                <DropdownMenuDivider />
                <DropdownMenuItem @click="runsheetAddStoppageTaskItem" icon="fa-stopwatch">Add Stoppage</DropdownMenuItem>
                <DropdownMenuItem @click="runsheetAddGeneralTaskItem" icon="fa-plus-circle">Add General Item</DropdownMenuItem>
              </ActionDropMenu>
            </div>
            <ContentTitle>Runsheet</ContentTitle>
          </div>
        </div>

        <TaskItemList
          ref="taskItemList"
          v-if="runSheetHasItems && !runSheet.loading"
          :task="task"
          :taskType="taskType"
          v-model="runSheet.taskItemList"
          :editingOrder="true"
          :editing="true"
          :showOrderActions="true"
          :isMergingTasks="isMergingTasks"
          :key="runSheet.updateKey"
          @removeTaskItem="removeTaskItem($event)"
          @removeTaskItemLinkedItem="removeTaskItemLinkedItem($event.taskItemId, $event.taskItemLinkedItemId)"
          @expandTaskItem="expandTaskItem($event)"
          @collapseTaskItem="collapseTaskItem($event)"
        />
        <div v-if="!runSheetHasItems && !runSheet.loading && !runSheet.error" class="text-center font-italic">At least one order, panel or purchase order must be selected before continuing</div>

      </FormWizardStep>

      <!-- Details -->
      <FormWizardStep stepid="details">
        <div v-show="runSheetHasItems || !requiresOrderSelection">
          <FormRow label="Add to Existing Task" v-if="canMergeTasks">
            <FormSelect v-model="mergeTaskId" :options="mergeTaskOptionList" note="Select an existing task to merge these orders into or select a new task" @input="mergeTaskIdUpdated" />
          </FormRow>
          <FormHorizontalRule v-if="hasNewTaskSelected && canMergeTasks" />

          <div v-show="hasNewTaskSelected">
            <FormRow label="Name">
              <FormInputText v-model="task.name" placeholder="Name" field="name" />
            </FormRow>
            <FormRow label="Description">
              <FormInputTextArea v-model="task.description" placeholder="Description" field="description" />
            </FormRow>
            <FormRow label="Start Date">
              <FormDateTime v-model="task.scheduleStartDate" field="scheduleStartDate" />
            </FormRow>
            <FormRow label="Labels">
              <FormLabelTag popupName="Task Labels" v-model="task.labelList" labelType="task" field="labelList" />
            </FormRow>

            <div v-if="!requiresOrderSelection && isLocationTrackingEnabled">
              <FormHorizontalRule />
              <FormRow label="Address">
                <FormInputAddressSearch placeholder="Address" v-model="taskAddress" :splitAddressComponents="false" />
              </FormRow>
              
              <FormRow label="Route Start Location" :fieldCount="2">
                <template v-slot:field1>
                  <FormSelect v-model="task.routeStartLocationType" :options="TaskRouteLocationTypeLookup" field="routeStartLocationType" note="Select the location type for the start of the route" />
                </template>
                <template v-slot:field2>
                  <FormInputAddressSearch v-show="task.routeStartLocationType == TaskRouteLocationTypes.address" v-model="task.routeStartLocationAddress" :splitAddressComponents="false" placeholder="Address" field="routeStartLocationAddress" />
                  <FormSelect v-show="task.routeStartLocationType == TaskRouteLocationTypes.accountLocation" v-model="task.startPointAccountLocationId" :options="accountLocationLookup" field="startPointAccountLocationId" />
                </template>
              </FormRow>

              <FormRow label="Route End Location" :fieldCount="2">
                <template v-slot:field1>
                  <FormSelect v-model="task.routeEndLocationType" :options="TaskRouteLocationTypeLookup" field="routeEndLocationType"  note="Select the location type for the end of the route after completing the items" />
                </template>
                <template v-slot:field2>
                  <FormInputAddressSearch v-show="task.routeEndLocationType == TaskRouteLocationTypes.address" v-model="task.routeEndLocationAddress" :splitAddressComponents="false" placeholder="Address" field="routeEndLocationAddress" />
                  <FormSelect v-show="task.routeEndLocationType == TaskRouteLocationTypes.accountLocation" v-model="task.endPointAccountLocationId" :options="accountLocationLookup" field="endPointAccountLocationId" />
                </template>
              </FormRow>
            </div>

            <FormHorizontalRule />
            <FormRow label="Primary User" helpTip="The primary user is the main user assigned to the task" v-if="taskType.isPrimaryUserRequired">
              <FormSelect :options="userList" v-model="task.primaryUserId" placeholder="Primary User" field="primaryUserId" />
            </FormRow>
            <FormRow label="Assigned Users" helpTip="These users all have access to the task for loading / signoff">
              <FormMultiSelect :options="userList" v-model="selectedUsers" placeholder="Users" field="users" />
            </FormRow>
            <FormRow label="Assigned Resources">
              <FormMultiSelect :options="resourceList" v-model="selectedResources" placeholder="Resources" field="resources" />
            </FormRow>

            <FormHorizontalRule />
            <FormRow label="Alerts" helpTip="Requires at least 1 user resource assigned to the task to send alerts">
              <FormStaticLabel>
                <TaskAlertList v-model="task.taskAlerts" :editing="true" />
              </FormStaticLabel>
            </FormRow>
          </div>
        </div>

        <div v-if="!runSheetHasItems && requiresOrderSelection" class="text-center font-italic">At least one order or transfer must be selected before continuing</div>
      </FormWizardStep>

      <!-- Map / Route -->
      <FormWizardStep v-if="isLocationTrackingEnabled" stepid="route">
        <div v-if="!runSheetHasItems && requiresOrderSelection" class="text-center font-italic">At least one order or transfer must be selected before continuing</div>

          <TaskManagerMapView 
            ref="mapView"
            v-if="runSheetHasItems || !requiresOrderSelection"
            :loadingText="taskRoute.calculating ? 'Recalculating Route...' : ''"
            :errorText="taskRoute.error"
            :displayRoutes="false"
            :displayUserLocations="true"
            :adjustBoundsOnLoad="false"
            :displayOrders="false"
            @mapLoaded="onMapViewLoaded">

            <template slot-scope="{ google, map }">
              <TaskMapRoute
                routeColour="#C7E4FF"
                :polylineCode="taskRoute.polylineCode"
                :itemPoints="taskRoute.itemPoints"
                :showItemPointNumbers="true"
                :google="google"
                :map="map"
                />
            </template>
          </TaskManagerMapView>
      </FormWizardStep>

      <!-- Review -->
      <FormWizardStep stepid="review">
        <LoadingPanel :loading="runSheet.loading" :loadingError="runSheet.error" loadingText="Loading Data" />

        <div v-if="!runSheetHasItems && requiresOrderSelection" class="text-center font-italic">At least one order, panel or purchase order must be selected before continuing</div>

        <div class="review-data" v-if="(runSheetHasItems || !requiresOrderSelection) && !runSheet.loading">
          <div class="row">
            <div class="col-6">
              <div class="review-header">
                <h2>Details Summary</h2> <a class="review-edit-button" @click="displayDetailsTab"><i class="far fa-pencil"></i></a>
              </div>

              <div v-show="hasNewTaskSelected">
                <FormRow label="Name" :labelColumnSize="5">
                  <FormStaticLabel>{{task.name | formatHyphenEmptyValue}}</FormStaticLabel>                        
                </FormRow>
                <FormRow label="Description" :labelColumnSize="5">
                  <div style="white-space: pre;">{{task.description | formatHyphenEmptyValue}}</div>
                </FormRow>
                <FormRow label="Start Time" :labelColumnSize="5">
                  <FormStaticLabel>{{formatDateTime(task.scheduleStartDate)}}</FormStaticLabel>                        
                </FormRow>
                <FormRow label="Assigned Users" :labelColumnSize="5">
                  <FormStaticLabel v-if="reviewSelectedUserList.length == 0">No Users Selected</FormStaticLabel>
                  <FormStaticLabel>{{reviewSelectedUserList.join(', ')}}</FormStaticLabel>
                </FormRow>
                <FormRow label="Assigned Resouces" :labelColumnSize="5">
                  <FormStaticLabel v-if="reviewSelectedResourceList.length == 0">No Resources Selected</FormStaticLabel>
                  <FormStaticLabel>{{reviewSelectedResourceList.join(', ')}}</FormStaticLabel>
                </FormRow>
                <FormRow label="Alerts" :labelColumnSize="5">
                  <FormStaticLabel>{{task.taskAlerts.length}} Alert{{displayCountPlural(task.taskAlerts.length)}}</FormStaticLabel>
                </FormRow>
              </div>

              <div v-if="!hasNewTaskSelected && mergeTargetTask">
                <FormRow label="Merging with Existing Task" :labelColumnSize="5">
                  <FormStaticLabel><LinkTask :id="mergeTargetTask.id" :name="`${mergeTargetTask.sequenceNumber} - ${mergeTargetTask.name}`" /></FormStaticLabel>
                </FormRow>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-12" v-if="runSheetHasItems">
              <div class="review-header">
                <h2>Runsheet Summary</h2> <a class="review-edit-button" @click="displayRunsheetTab"><i class="far fa-pencil"></i></a>
              </div>

              <TaskItemList
                v-if="runSheetHasItems && !runSheet.loading"
                :task="task"
                :taskType="taskType"
                :value="runSheet.taskItemList"
                :editingOrder="false"
                :showOrderActions="false"
                :isMergingTasks="isMergingTasks"
              />
            </div>
          </div>
        </div>
      </FormWizardStep>

    </template>
  </WizardDialog>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'

import Utils from '@/utils/utils'

import FormMixin from '@/mixins/form'
import TaskDespatchMixin from '@/modules/tasks/mixins/taskDespatchMixin'
import WizardDialog from '@/components/dialog/wizardDialog.vue'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'

import AccountLocationSelect from '@/modules/admin/components/accountLocationSelect'
import ContactSearchableSelect from '@/modules/contacts/components/contactSearchableSelect' 
import TaskDespatchOrderDataTable from '@/modules/tasks/components/taskDespatchOrderDataTable'
import TaskPanelTransferDataTable from '@/modules/tasks/components/taskPanelTransferDataTable'
import TaskStockTransferDataTable from '@/modules/tasks/components/taskStockTransferDataTable'
import TaskPurchaseOrderPickupDataTable from '@/modules/tasks/components/taskPurchaseOrderPickupDataTable'

import TaskItemList from '@/modules/tasks/components/taskItemList'
import TaskAlertList from '@/modules/tasks/components/taskAlertList'
import TaskMapRoute from '@/modules/tasks/components/taskMapRoute'
import TaskManagerMapView from '@/modules/tasks/components/taskManagerMapView'

import { TaskItemTypes, TaskStatusTypes, ResourceTypes, TaskRouteLocationTypes, TaskRouteLocationTypeLookup, TaskItemStatusTypes, DescriptorLookupTypes, AccountLabelTypes } from '@/store/types'


export default {
  name: 'TaskNewDialog',
  mixins: [
    ModalDialogMixin,
    FormMixin,
    TaskDespatchMixin
  ],
  components: {
    WizardDialog,
    TaskAlertList,
    AccountLocationSelect,
    ContactSearchableSelect,
    TaskDespatchOrderDataTable,
    TaskPanelTransferDataTable,
    TaskStockTransferDataTable,
    TaskPurchaseOrderPickupDataTable,
    TaskMapRoute,
    TaskManagerMapView,
    TaskItemList
  },
  props: {
    taskTypeId: Number,
    accountLocationId: Number,
    contactId: Number,
    contactName: String,
    orderIdList: Array,
    purchaseOrderIdList: Array
  },
  data() {
    return {
      responseInfo: {},
      dialogForm: {},

      taskType: {},
      submitting: false,
      loading: true,
      loadingError: '',

      selectContactId: this.contactId,    // For task types with a single contact required

      // Available tasks to merge with for the same task type
      mergeTaskId: null,
      mergeTaskSelectDefault: true,
      availableTaskList: [],

      userList: [],
      resourceList: [],
      selectedUsers: [],
      selectedResources: [],
      addressLookupList: [],
      accountLocationList: [],

      taskAddress: '',

      selectedOrderIdList: [],
      ignoreOrderItemIdList: [],

      selectedPanelIdList: [],
      panelList: [],

      selectedPickupPOIdList: [],
      selectedDespatchPOIdList: [],
      ignorePOItemIdList: [],

      selectedStockTransferList: [],

      runSheet: {
        loading: false,
        error: '',
        updateKey: 0,
        taskItemList: [],
        orderList: []
      },

      taskRoute: {
        calculating: false,
        error: '',
        polylineCode: '',
        itemPoints: []
      },

      task: {
        taskTypeId: this.taskTypeId,
        name: '',
        description: '',
        highlightColour: '#000000',
        accountLocationId: this.accountLocationId,
        routeStartLocationType: TaskRouteLocationTypes.accountLocation,
        routeEndLocationType: TaskRouteLocationTypes.accountLocation,
        startPointAccountLocationId: this.accountLocationId,
        endPointAccountLocationId: this.accountLocationId,
        routeStartLocationAddress: '',
        routeEndLocationAddress: '',
        scheduleStartDate: moment().toISOString(),
        scheduleEndDate: moment().add(30, 'minutes').toISOString(),
        primaryUserId: null,
        resources: [],
        users: [],
        taskAlerts: [],
        labelList: [],
        taskItems: []
      }
    }
  },
  watch: {
    selectContactId: function() {
      this.selectedOrderIdList = []
      this.selectedPanelIdList = []
      this.selectedPickupPOIdList = []
      this.selectedDespatchPOIdList = []
      this.mergeTaskId = null

      this.orderListUpdated()
      this.panelListUpdated()
      this.purchaseOrderListUpdated();
      this.requestExistingTasks()
    },
    mergeTaskId: function() {
      this.clearRunsheet()
      this.buildRunsheet()
    },
    'task.taskTypeId': function() {
      this.reloadTaskType()
    },
    'task.accountLocationId': function() {
      this.selectedOrderIdList = []
      this.orderListUpdated()
      this.requestExistingTasks()

      // Update route start/end location details
      if(this.task.accountLocationId) {
        this.task.startPointAccountLocationId = this.task.accountLocationId
        this.task.endPointAccountLocationId = this.task.accountLocationId
      }
    }
  },
  created() {
    this.TaskRouteLocationTypes = TaskRouteLocationTypes
    this.TaskRouteLocationTypeLookup = TaskRouteLocationTypeLookup
  },
  mounted() {    
  },
  computed: {
    hasNewTaskSelected() {
      return !this.mergeTaskId
    },
    isMergingTasks() {
      return !!this.mergeTaskId
    },
    canMergeTasks() {
      // Only merge tasks for order based tasks
      return this.requiresOrderSelection
    },
    finishDisabled() {
      if(!this.runSheetHasItems && this.requiresOrderSelection)
        return true
      if(this.isSingleContactTaskType && !this.hasContactSelected)
        return true
      return false
    },
    mergeTaskOptionList() {
      let lookupList = [{ value: null, name: `New ${this.taskTypeName} Task` }]

      lookupList = _.concat(lookupList, this.availableTaskList.map(task => {
        return {
          value: task.id,
          name: `${task.sequenceNumber} - ${task.name}`
        }
      }))

      return lookupList
    },
    mergeTargetTask() {
      if(this.mergeTaskId)
        return this.availableTaskList.find(task => task.id == this.mergeTaskId)
      return null
    },
    runSheetHasItems() {
      return this.runSheet.taskItemList.length > 0
    },
    hasTaskType() {
      return this.task.taskTypeId != null
    },
    taskTypeName() {
      return this.taskType.name
    },
    title() {
      return `New Task`
    },
    icon() {
      return `fa-calendar-day`
    },
    filterContactId() {
      if(this.isSingleContactTaskType)
        return this.selectContactId
      return null
    },
    isSingleContactTaskType() {
      return this.taskType.isSingleContact
    },
    requiresOrderSelection() {
      return this.taskType.isDespatchEnabled && (this.taskType.isOrderItemDespatchEnabled || this.taskType.isOrderCompleteEnabled)
    },
    hasPanelTransferSelection() {
      return this.taskType.isTransferEnabled
    },
    hasStockTransferSelection() {
      if(!this.taskType.isTransferEnabled)
        return false

      // If only 1 location, hide stock transfer
      if(this.accountLocationList.length < 2)
        return false

      return true
    },
    hasPurchaseOrderPickupSelection() {
      return this.taskType.isTransferEnabled && this.userHasReadPermission(this.PermissionTypes.PurchasingPermissionTypes.purchaseOrder)
    },
    isLocationTrackingEnabled() {
      return this.taskType.isLocationTrackingEnabled
    },
    hasContactSelected() {
      return !!this.selectContactId
    },
    submitTitle() {
      if(this.hasNewTaskSelected)
        return 'Create'
      return 'Add'
    },
    wizardTabList() {
      // Steps based on task type & info provided
      var list = [{ title: 'Task Info', id: 'tasktype' }]

      if(this.requiresOrderSelection)
        list.push({ title: 'Orders', id: 'orders' })
      if(this.hasPanelTransferSelection)
        list.push({ title: 'Panel Transfer', id: 'panels' })
      if(this.hasStockTransferSelection)
        list.push({ title: 'Stock Transfer', id: 'stock' })
      if(this.hasPurchaseOrderPickupSelection)
        list.push({ title: 'Purchase Order Pickup', id: 'popickup' })
      if(this.requiresOrderSelection || this.hasPanelTransferSelection || this.hasStockTransferSelection || this.hasPurchaseOrderPickupSelection)
        list.push({ title: 'Runsheet', id: 'runsheet' })

      list.push({ title: 'Details', id: 'details' })
      if(this.isLocationTrackingEnabled)
        list.push({ title: 'Route', id: 'route' })
      list.push({ title: 'Review', id: 'review' })

      return list
    },
    accountLocationLookup() {
      return this.accountLocationList.map(location => {
        return { name: `${location.name} - ${location.formattedAddress}`, value: location.id }
      })
    },
    reviewSelectedUserList() {
      return this.selectedUsers.map(id => Utils.getLookupName(this.userList, id))
    },
    reviewSelectedResourceList() {
      return this.selectedResources.map(id => Utils.getLookupName(this.resourceList, id))
    },
    activeTaskTypeLookup() {
      return this.taskTypeList.filter(taskType => taskType.isActive).map(taskType => {
        return {
          value: taskType.id,
          name: taskType.name
        }
      })
    }
  },
  methods: {
    show(params) {
      // Reset form
      this.responseInfo = { }
      this.dialogForm = { }

      if(this.orderIdList)
        this.selectedOrderIdList = this.orderIdList
      if(this.purchaseOrderIdList)
        this.selectedPickupPOIdList = this.purchaseOrderIdList

      this.requestData()
      this.reloadTaskType()

      return this.$refs.dialog.show(params)
    },
    requestData() {
      this.loading = true
      this.loadingError = ''

      Promise.all([
        this.$apiService.tasksService.getResourceList(),
        this.$apiService.userService.getUserList(),
        this.$apiService.adminService.getDescriptorLookupListForType(DescriptorLookupTypes.addressLookup),
        this.$apiService.adminService.getAccountLocations(),
        this.$apiService.tasksService.getTaskTypeList()
      ])
      .then(results => {
        this.addressLookupList = results[2].data
        this.accountLocationList = results[3].data.filter(location => location.isActive)
        this.taskTypeList = results[4].data

        // Default task type if not set
        let activeTaskTypes = this.taskTypeList.filter(taskType => taskType.isActive)
        if(!this.task.taskTypeId && activeTaskTypes.length > 0) {
          this.task.taskTypeId = activeTaskTypes[0].id
        }

        // Update task info
        if(this.contactName && this.isSingleContactTaskType)
          this.task.name = `${this.contactName}`

        this.task.highlightColour = this.taskType.highlightColour

        this.resourceList = results[0].data
        .filter(resource => resource.isActive && resource.type == ResourceTypes.general)
        .map(resource => {
          return {
            value: resource.id,
            name: resource.name,
            colour: resource.highlightColour
          }
        }) 
        
        this.userList = results[1].data
        .filter(user => user.isActive && user.isTaskResource)
        .map(user => {
          return {
            value: user.id,
            name: user.name,
            colour: user.highlightColour
          }
        })


        this.selectedUsers = []
        if(this.taskType.defaultUsers)
          this.selectedUsers = this.taskType.defaultUsers.map(user => user.id)

        // Default to specified location
        // Note: We force start & end location to be delivery depot for deliveries
        this.task.accountLocationId = this.$props.accountLocationId
        this.task.startPointAccountLocationId = this.task.accountLocationId
        this.task.endPointAccountLocationId = this.task.accountLocationId

        let deliveryDepotLocation = this.accountLocationList.find(accLocation => {
          if(!accLocation.labelList)
            return false
          return accLocation.labelList.find(label => label.statusType == AccountLabelTypes.deliveryDepot) != null
        })

        // Only use default if none specified
        // We'll use order account location (if creating a task from an order) by default
        if(!this.task.accountLocationId) {
          if(deliveryDepotLocation) {
            this.task.accountLocationId = deliveryDepotLocation.id
            this.task.startPointAccountLocationId = deliveryDepotLocation.id
            this.task.endPointAccountLocationId = deliveryDepotLocation.id
          } else {
            // Use any location list
            if(this.accountLocationList.length > 0) {
              this.task.accountLocationId = this.accountLocationList[0].id
              this.task.startPointAccountLocationId = this.accountLocationList[0].id
              this.task.endPointAccountLocationId = this.accountLocationList[0].id
            }
          }
        }
      })
      .catch(err => {
        this.loadingError = this.processFormResponse(err).errorMessage
        this.$refs.dialog.displayErrors()
      })
      .finally(() => {
        this.loading = false

        this.$nextTick(() => {
          // Update counts if anything added on display
          this.updateRunsheetCounts()

          // Jump straight to details tab if orders/purchase orders already selected when displaying this dialog
          if((this.orderIdList && this.orderIdList.length > 0) || (this.purchaseOrderIdList && this.purchaseOrderIdList.length > 0)) {
            this.displayDetailsTab()
          }
        })
      })
    },
    reloadTaskType() {
      if(this.task.taskTypeId == null)
        return

      this.$apiService.tasksService.getTaskType(this.task.taskTypeId)
      .then(response => {
        this.taskType = response.data
        // Load the existing task list once we have TaskType data
        this.requestExistingTasks()

        // Update defaults
        this.selectedUsers = this.taskType.defaultUsers.map(user => user.id)
      })
      .catch(err => {
        this.loadingError = this.processFormResponse(err).errorMessage
        this.$refs.dialog.displayErrors()
      })
      .finally(() => {
      })
    },
    onClosed() {
      this.$refs.dialog.close(false)
    },
    onSubmitted() {
      this.submitting = true
      this.dialogForm = this.clearFormResponse()

      // If merging, just update task items for merge target task
      if(this.mergeTaskId) {
        this.submitTaskMerge()
        return
      }

      var taskCreateData = _.cloneDeep(this.task)

      taskCreateData = _.omit(taskCreateData, ['routeStartLocationAddress', 'startPointAccountLocationId', 'routeEndLocationAddress', 'endPointAccountLocationId'])

      // Transform the route start/end selections
      if(this.task.routeStartLocationType == TaskRouteLocationTypes.accountLocation)
        taskCreateData.startPointAccountLocationId = this.task.startPointAccountLocationId
      if(this.task.routeEndLocationType == TaskRouteLocationTypes.accountLocation)
        taskCreateData.endPointAccountLocationId = this.task.endPointAccountLocationId

      if(this.task.routeStartLocationType == TaskRouteLocationTypes.address)
        taskCreateData.routeStartLocationAddress = this.task.routeStartLocationAddress
      if(this.task.routeEndLocationType == TaskRouteLocationTypes.address)
        taskCreateData.routeEndLocationAddress = this.task.routeEndLocationAddress

      // TODO: Total time is taskItem total time + route total time
      // Make sure it isn't below 'defaultTaskTime'

      taskCreateData.scheduleEndDate = moment(this.task.scheduleStartDate).add(this.taskType.defaultTaskTime, 'minutes').toISOString(),
      taskCreateData.users = this.selectedUsers
      taskCreateData.resources = this.selectedResources
      taskCreateData.labelList = taskCreateData.labelList.map(label => label.id)

      // Make sure the primary user is in the user list too if set
      if(this.task.primaryUserId && !taskCreateData.users.includes(this.task.primaryUserId)) {
        taskCreateData.users.push(this.task.primaryUserId)
      }

      taskCreateData.taskItems = this.runSheet.taskItemList.map((itemInfo, index) => {
        var taskItemData = {
          type: itemInfo.type,
          name: itemInfo.name,
          details: itemInfo.details,
          sortOrder: index,
          isLocationEnabled: itemInfo.isLocationEnabled,
          locationAddress: itemInfo.locationAddress,
          locationCoordinate: null,     // Needed? Will be generated on server
          accountLocationId: itemInfo.accountLocationId,
          estimatedProcessingTime: itemInfo.estimatedProcessingTime,
          taskItemLinkedItems: itemInfo.taskItemLinkedItems
        }

        return taskItemData
      })

      // If not an order despatch based task type, just create a single generic task item
      if(!this.requiresOrderSelection) {
        taskCreateData.taskItems = [{
          type: TaskItemTypes.general,
          name: this.task.name,
          details: this.task.description,
          sortOrder: 0,
          isLocationEnabled: this.isLocationTrackingEnabled,
          locationAddress: this.taskAddress,
          estimatedProcessingTime: this.taskType.defaultTaskTime,
          taskItemLinkedItems: []
        }]
      }

      this.$apiService.tasksService.createTask(taskCreateData)
      .then(response => {
        this.successToast('Task Created')
        this.$refs.dialog.close(response.data.id)
      })
      .catch(err => {
        this.$data.dialogForm = this.processFormResponse(err)
        this.$refs.dialog.displayErrors()
      })
      .finally(() => {
        this.$data.submitting = false
      })
    },
    async submitTaskMerge() {
      this.submitting = true
      this.$data.dialogForm = this.clearFormResponse()

      try {
        let taskResponse = await this.$apiService.tasksService.getTask(this.mergeTaskId)
        let task = taskResponse.data

        let updateData = {
          notifyUsers: true
        }
        updateData.taskItems = this.runSheet.taskItemList.map((itemInfo, index) => {
          var taskItemData = {
            id: itemInfo.taskItemId,
            type: itemInfo.type,
            name: itemInfo.name,
            details: itemInfo.details,
            sortOrder: index,
            isLocationEnabled: itemInfo.isLocationEnabled,
            locationAddress: itemInfo.locationAddress,
            locationCoordinate: null,
            accountLocationId: itemInfo.accountLocationId,
            estimatedProcessingTime: itemInfo.estimatedProcessingTime,
            taskItemLinkedItems: itemInfo.taskItemLinkedItems
          }

          return taskItemData
        })

        // Update task item
        await this.$apiService.tasksService.updateTask(task.id, updateData)

        this.successToast('Task Merged')
        this.submitting = false          
        this.$refs.dialog.close(task.id)

      } catch(err) {
        // Process errors
        this.$data.dialogForm = this.processFormResponse(err)
        this.submitting = false
      }
    },
    requestExistingTasks() {
      this.mergeTaskId = null
      this.availableTaskList = []

      this.reloadExistingTaskList()
    },
    reloadExistingTaskList() {
      // Ignore if no contact set (and it's required)
      if(this.isSingleContactTaskType && !this.selectContactId) {
        this.mergeTaskId = null
        this.availableTaskList = []
        return
      }

      // Ignore if not a mergable task
      if(!this.canMergeTasks) {
        this.mergeTaskId = null
        this.availableTaskList = []
        return
      }

      let taskParams = {
        filter: {
          taskTypeId: this.task.taskTypeId,
          status: [TaskStatusTypes.pendingProduction, TaskStatusTypes.loading, TaskStatusTypes.ready]
        } 
      }
      if(this.isSingleContactTaskType)
        taskParams.filter.contactId = this.selectContactId

      // Get all tasks of the same type that are active for use to merge with
      this.$apiService.tasksService.getTaskList(taskParams)
      .then(response => {
        this.availableTaskList = response.data

        // If tasks available to merge with, default to first available
        // Only do for contact specific task types that uses despatch
        if(this.isSingleContactTaskType && this.requiresOrderSelection && this.mergeTaskSelectDefault) {
          if(this.availableTaskList.length > 0)
            this.mergeTaskId = this.availableTaskList[0].id
        }
      })
      .catch(err => {
        console.error(err)
      })
    },
    onContactInfoUpdated(contactInfo) { 
      // Update task name/description for contact 
      if(contactInfo) { 
        this.task.name = `${contactInfo.name} ${this.taskType.name}` 
        this.task.description = `${this.taskType.name} for ${contactInfo.name}` 
      } 
    },
    updateRunsheetCounts() {
      var tabIndex = this.wizardTabList.findIndex(tab => tab.id == 'runsheet')

      this.$refs.dialog.clearResultCounts()
      this.$refs.dialog.setResultCount(tabIndex, this.selectedOrderIdList.length + this.selectedPanelIdList.length + this.selectedPickupPOIdList.length + this.selectedDespatchPOIdList.length + this.selectedStockTransferList.length)
    },
    orderListUpdated() {
      this.updateRunsheetCounts()
    },
    panelListUpdated() {
      this.updateRunsheetCounts()
    },
    purchaseOrderListUpdated() {
      this.updateRunsheetCounts()
    },
    wizardStepChanged(tabIndex) {
      // Clear the ignore item list if going back to the relevant tabs
      if(this.wizardTabList[tabIndex] && this.wizardTabList[tabIndex].id == 'orders')
        this.ignoreOrderItemIdList = []
      if(this.wizardTabList[tabIndex] && this.wizardTabList[tabIndex].id == 'popickup')
        this.ignorePOItemIdList = []

      // Always update run sheet
      this.buildRunsheet()

      if(this.wizardTabList[tabIndex] && this.wizardTabList[tabIndex].id == 'details')
        this.reloadExistingTaskList()

      if(this.wizardTabList[tabIndex] && this.wizardTabList[tabIndex].id == 'route')
        this.recalculateTaskNavigationRoute()
    },
    mergeTaskIdUpdated() {
      // Once changed, no longer select the default merge task again
      this.mergeTaskSelectDefault = false
    },
    addStockTransferToRunsheet(transferInfo) {
      this.selectedStockTransferList.push({
        key: `${transferInfo.stockId}-${transferInfo.accountLocationId}-${transferInfo.glassLevelId}`,
        type: transferInfo.type,
        stockId: transferInfo.stockId,
        stock: transferInfo.stock,
        accountLocationId: transferInfo.accountLocationId,
        isLoadedAtLocation: transferInfo.isLoadedAtLocation,
        glassLevelId: transferInfo.glassLevelId,
        glassLevel: transferInfo.glassLevel,
        quantity: transferInfo.quantity
      })

      this.updateRunsheetCounts()
    },
    clearRunsheet() {
      this.runSheet.taskItemList = []
    },
    buildRunsheet() {
      // Load all the order details
      // Group by address

      // If not linked to orders, ignore this
      if(!this.requiresOrderSelection)
        return

      this.runSheet.loading = true
      this.runSheet.error = ''

      this.generateTaskItemList(this.taskType, this.task.accountLocationId, this.runSheet.taskItemList, this.selectedOrderIdList, this.ignoreOrderItemIdList, this.ignorePOItemIdList, this.selectedPanelIdList, this.selectedStockTransferList, this.selectedPickupPOIdList, this.selectedDespatchPOIdList, this.isMergingTasks ? this.mergeTaskId : null, this.addressLookupList)
      .then(taskItems => {
        this.runSheet.taskItemList = taskItems
        this.$forceUpdate()
      })
      .catch(err => {
        this.runSheet.error = this.processFormResponse(err).errorMessage
        console.error(err)
      })
      .finally(() => {
        this.runSheet.loading = false
      })
    },
    removeTaskItem(taskItemId) {
      var taskItemIndex = this.runSheet.taskItemList.findIndex(taskItem => taskItem.id == taskItemId)
      if(taskItemIndex == -1)
        return

      this.runSheet.taskItemList.splice(taskItemIndex, 1)

      this.cleanupSelectedItems()
      this.runSheet.updateKey++
    },
    removeTaskItemLinkedItem(taskItemId, linkedItemId) {
      var taskItemIndex = this.runSheet.taskItemList.findIndex(taskItem => taskItem.id == taskItemId)
      if(taskItemIndex == -1)
        return

      var taskItem = this.runSheet.taskItemList[taskItemIndex]

      var removeLinkedItem = taskItem.taskItemLinkedItems.find(linkedItem => linkedItem.id == linkedItemId)
      if(!removeLinkedItem)
        return

      // Add the order item / PO id to the do-not-add list
      // We'll clear the do-not-add list if the user goes back to the first tab
      if(removeLinkedItem.orderItemId) {
        this.ignoreOrderItemIdList.push(removeLinkedItem.orderItemId)
      }
      if(removeLinkedItem.purchaseOrderItemId) {
        this.ignorePOItemIdList.push(removeLinkedItem.purchaseOrderItemId)
      }

      taskItem.taskItemLinkedItems = taskItem.taskItemLinkedItems.filter(linkedItem => linkedItem.id != linkedItemId)

      this.cleanupSelectedItems()

      // If task item is empty, remove the task item
      if(taskItem.taskItemLinkedItems.length == 0) {
        this.removeTaskItem(taskItemId)
      }      

      this.runSheet.updateKey++
    },
    cleanupSelectedItems() {
      // Collate all linked items & remove any no longer referenced
      var linkedOrderId = []
      var linkedPurchaseOrderId = []
      var linkedPanelId = []
      var linkedStockTransfer = []
      this.runSheet.taskItemList.forEach(taskItem => {
        taskItem.taskItemLinkedItems.forEach(linkedItem => {
          if(linkedItem.orderId)
            linkedOrderId.push(linkedItem.orderId)
          if(linkedItem.productionPanelId)
            linkedPanelId.push(linkedItem.productionPanelId)

          if(taskItem.type == TaskItemTypes.transferInternal || taskItem.type == TaskItemTypes.transferExternal) {
            linkedStockTransfer.push({
              key: `${linkedItem.stockId}-${taskItem.accountLocationId}-${linkedItem.glassLevelId}`
            })
          }
          if(taskItem.type == TaskItemTypes.transferPickup) {
            if(linkedItem.purchaseOrderId)
              linkedPurchaseOrderId.push(linkedItem.purchaseOrderId)
          }
        })
      })

      this.selectedOrderIdList = _.intersection(this.selectedOrderIdList, linkedOrderId)
      this.selectedPickupPOIdList = _.intersection(this.selectedPickupPOIdList, linkedPurchaseOrderId)
      this.selectedDespatchPOIdList = _.intersection(this.selectedDespatchPOIdList, linkedPurchaseOrderId)
      this.selectedPanelIdList = _.intersection(this.selectedPanelIdList, linkedPanelId)
      this.selectedStockTransferList = _.intersectionBy(this.selectedStockTransferList, linkedStockTransfer, 'key')
    },
    runsheetExpandAll() {
      this.runSheet.taskItemList.forEach(taskItem => {
        this.$set(taskItem, 'expanded', true)
      })
      this.runSheet.updateKey++
    },
    runsheetCollapseAll() {
      this.runSheet.taskItemList.forEach(taskItem => {
        taskItem.expanded = false
      })
      this.runSheet.updateKey++
    },
    runsheetAddStoppageTaskItem() {
      this.runSheet.taskItemList.push({
        id: uuidv4(),
        type: TaskItemTypes.stoppage,
        name: 'Break',
        details: '',
        key: '',
        expanded: false,
        warningList: [],
        estimatedProcessingTime: 30,      // Default 30min break
        isLocationEnabled: false,
        locationAddress: '',
        taskItemLinkedItems: []
      })
      this.runSheet.updateKey++
    },
    runsheetAddGeneralTaskItem() {
      this.runSheet.taskItemList.push({
        id: uuidv4(),
        type: TaskItemTypes.general,
        name: 'Misc',
        details: '',
        key: '',
        expanded: false,
        warningList: [],
        estimatedProcessingTime: 30,      // Default 30min task
        isLocationEnabled: this.isLocationTrackingEnabled,
        locationAddress: '',
        taskItemLinkedItems: []
      })
      this.runSheet.updateKey++
    },
    expandTaskItem(taskItemId) {
      var taskItem = this.runSheet.taskItemList.find(taskItem => taskItem.id == taskItemId)
      if(taskItem)
        taskItem.expanded = true
    },
    collapseTaskItem(taskItemId) {
      var taskItem = this.runSheet.taskItemList.find(taskItem => taskItem.id == taskItemId)
      if(taskItem)
        taskItem.expanded = false
    },

    recalculateTaskNavigationRoute() {
      if(!this.isLocationTrackingEnabled)
        return

      var taskInfo = {
        resources: this.selectedResources,
        users: this.selectedUsers,
        taskItems: [],
        routeStartLocationType: this.task.routeStartLocationType,
        routeEndLocationType: this.task.routeEndLocationType,
        startPointAccountLocationId: this.task.startPointAccountLocationId,
        endPointAccountLocationId: this.task.endPointAccountLocationId,
      }

      if(this.task.routeStartLocationType == TaskRouteLocationTypes.accountLocation)
        taskInfo.startPointAccountLocationId = this.task.startPointAccountLocationId
      if(this.task.routeEndLocationType == TaskRouteLocationTypes.accountLocation)
        taskInfo.endPointAccountLocationId = this.task.endPointAccountLocationId

      if(this.task.routeStartLocationType == TaskRouteLocationTypes.address)
        taskInfo.routeStartLocationAddress = this.task.routeStartLocationAddress
      if(this.task.routeEndLocationType == TaskRouteLocationTypes.address)
        taskInfo.routeEndLocationAddress = this.task.routeEndLocationAddress

      taskInfo.taskItems = _.compact(this.runSheet.taskItemList.map(itemInfo => {
        if(!itemInfo.isLocationEnabled)
          return null
        return {
          locationAddress: itemInfo.locationAddress
        }
      }))

      // If not linked to orders, use basic address for the 1st task item
      if(!this.requiresOrderSelection) {
        taskInfo.taskItems = [{
          locationAddress: this.taskAddress
        }]
      }

      this.taskRoute.calculating = true
      this.taskRoute.error = ''

      this.$apiService.tasksService.calculateTaskRoute(taskInfo)
      .then(response => {
        this.taskRoute.itemPoints = []
        this.taskRoute.polylineCode = ''

        if(!response.data) {
          this.taskRoute.error = 'Insufficient route points. Check starting location / task items'
        }
        else {
          this.taskRoute.polylineCode = response.data.polyline
          this.taskRoute.itemPoints = []

          response.data.legs.forEach((leg, index) => {
            if(leg.steps.length > 0) {
              if(index == 0) {
                this.taskRoute.itemPoints.push({
                  lng: leg.steps[0].startLocation.x,
                  lat: leg.steps[0].startLocation.y
                })
              }
              this.taskRoute.itemPoints.push({
                lng: leg.steps[leg.steps.length-1].endLocation.x,
                lat: leg.steps[leg.steps.length-1].endLocation.y
              })
            }
          })

          if(this.taskRoute.itemPoints.length <= 1)
            this.taskRoute.error = 'Insufficient route points. Check starting location / task items'
        }

        // Fit to the route
        this.$nextTick(() => {
          if(this.taskRoute.itemPoints.length >= 2) {
            if(this.$refs.mapView)
              this.$refs.mapView.panToFitPointList(this.taskRoute.itemPoints)
          }
        })
      })
      .catch(err => {
        this.taskRoute.error = this.processFormResponse(err).errorMessage
      })
      .finally(() => {
        this.taskRoute.calculating = false
      })
    },
    onMapViewLoaded() {
      this.mapLoaded = true

      this.$nextTick(() => {
        if(this.taskRoute.itemPoints.length >= 2) {
          if(this.$refs.mapView)
            this.$refs.mapView.panToFitPointList(this.taskRoute.itemPoints)
        }
      })
    },

    displayOrdersTab() {
      var tabIndex = this.wizardTabList.findIndex(tab => tab.id == 'orders')
      this.$refs.dialog.selectTab(tabIndex)
    },
    displayRunsheetTab() {
      var tabIndex = this.wizardTabList.findIndex(tab => tab.id == 'runsheet')
      this.$refs.dialog.selectTab(tabIndex)
    },
    displayDetailsTab() {
      var tabIndex = this.wizardTabList.findIndex(tab => tab.id == 'details')
      this.$refs.dialog.selectTab(tabIndex)
    },
  }
}
</script>